import React from 'react';
import { LucideIcon, MoreVertical, Loader2 } from 'lucide-react';
import { Button } from '@/ui-components/ui/button';
import { cn } from '@/lib/utils/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/util-components/dropdown-menu';

export interface PrimaryActionDropdownItem {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  className?: string;
}

interface PrimaryActionDropdownProps {
  triggerIcon?: LucideIcon;
  triggerLabel?: string;
  actions: PrimaryActionDropdownItem[];
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  variant?: "dark" | "blue";
  align?: "start" | "center" | "end";
}

/**
 * Reusable primary action dropdown button.
 * Design-coherent with PrimaryActionButton and DataViewToolbar.
 */
export const PrimaryActionDropdown = ({
  triggerIcon: Icon,
  triggerLabel,
  actions,
  className,
  disabled,
  isLoading,
  variant = "dark",
  align = "end"
}: PrimaryActionDropdownProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          disabled={disabled || isLoading}
          size="sm"
          className={cn(
            "h-9 px-3 text-sm font-bold transition-all active:scale-95 shadow-sm rounded-xl min-w-0",
            variant === "dark" 
              ? "bg-gray-900 text-white hover:bg-gray-800" 
              : "bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/10",
            !triggerLabel && "px-3", 
            className
          )}
        >
          {isLoading ? (
            <Loader2 className={cn("h-4 w-4 animate-spin shrink-0", triggerLabel && "sm:mr-2")} />
          ) : Icon ? (
            <Icon className={cn("h-4 w-4 shrink-0", triggerLabel && "sm:mr-2")} />
          ) : (
            <MoreVertical className={cn("h-4 w-4 shrink-0", triggerLabel && "sm:mr-2")} />
          )}
          {triggerLabel && <span className={cn("truncate", (isLoading || !!Icon) && "hidden sm:inline")}>{triggerLabel}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56 p-1 rounded-xl border-gray-200 shadow-xl z-50">
        {actions.map((action, index) => {
          const ActionIcon = action.icon;
          return (
            <DropdownMenuItem
              key={index}
              onClick={action.onClick}
              className={cn(
                "text-sm rounded-lg px-3 py-2 cursor-pointer text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:text-gray-900",
                action.className
              )}
            >
              {ActionIcon && <ActionIcon className="mr-2 h-4 w-4 text-gray-500" />}
              {action.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
